import { Stack } from "@mui/material";
import { LDSToast } from "components/LDSToast";
import { LoadingOrEmptyWrapper } from "components/LoadingAndEmptyHandler";
import { ExecutionStatusChip } from "features/shared/components/ExecutionStatusChip";
import { ExecutionStatus } from "features/shared/model/types";
import { useTranslation } from "react-i18next";
import { convertDatetimeStringToDate } from "utils/date";

import type { ExecutionStep, IfcScheduleProgress } from "../../model/types";

import { ExecutionStepItem } from "./ExecutionStepItem";
import { ScheduleProgressSection } from "./ScheduleProgressSection";

type ExecutionStepsContentProps = {
  completedAt?: string;
  createdAt?: string;
  errorMessage?: string;
  executedAt?: string;
  infoMessage?: string;
  progressPercent?: null | number;
  scheduleProgress?: IfcScheduleProgress | null;
  showHeader?: boolean;
  status?: ExecutionStatus;
  steps?: ExecutionStep[];
};

export const ExecutionStepsContent = ({
  completedAt,
  createdAt,
  errorMessage,
  executedAt,
  infoMessage,
  progressPercent,
  scheduleProgress,
  showHeader = true,
  status,
  steps = [],
}: ExecutionStepsContentProps) => {
  const { t } = useTranslation();
  const toastMessageProps = { className: "mt-3", showDismiss: false, showIcon: false, showUndo: false };
  const hasRenderableContent =
    steps.length > 0 ||
    status != null ||
    progressPercent != null ||
    Boolean(createdAt || completedAt || executedAt) ||
    Boolean(errorMessage || infoMessage) ||
    Boolean(scheduleProgress);

  return (
    <LoadingOrEmptyWrapper height="60vh" showEmpty={!hasRenderableContent} showLoading={false}>
      <div className="text-left">
        {showHeader ? (
          <>
            <h2 className="font-ds-headline-lg">{t("Execution.steps.title")}</h2>
            <p className="mt-1 text-lds-foreground-muted font-ds-body-regular-xs">{t("Execution.steps.description")}</p>
          </>
        ) : null}
        {status ? (
          <Stack className={`${showHeader ? "mt-4" : ""} items-center gap-3`}>
            <ExecutionStatusChip status={status} />
            {progressPercent != null && (
              <span className="text-lds-foreground-secondary font-ds-body-regular-xs">
                {t("Execution.steps.progress", { value: progressPercent })}
              </span>
            )}
          </Stack>
        ) : null}
        {(createdAt || completedAt || executedAt) && (
          <Stack className="mt-3 gap-2 text-lds-foreground-secondary">
            {createdAt ? (
              <p className="font-ds-body-regular-xs">
                {t("general.createdAt")} {convertDatetimeStringToDate(createdAt)}
              </p>
            ) : null}
            {completedAt ? (
              <p className="font-ds-body-regular-xs">
                {t("general.completedAt")} {convertDatetimeStringToDate(completedAt)}
              </p>
            ) : null}
            {executedAt ? (
              <p className="font-ds-body-regular-xs">
                {t("general.executedAt")} {convertDatetimeStringToDate(executedAt)}
              </p>
            ) : null}
          </Stack>
        )}
        <div className="mt-6">
          {steps.map((step, index) => (
            <ExecutionStepItem isLast={index === steps.length - 1} key={step.key} order={index + 1} step={step} />
          ))}
        </div>
        {errorMessage ? <LDSToast message={errorMessage} {...toastMessageProps} variant="error" /> : null}
        {infoMessage && !errorMessage ? (
          <LDSToast message={infoMessage} {...toastMessageProps} variant="neutral" />
        ) : null}
        {scheduleProgress ? <ScheduleProgressSection progress={scheduleProgress} /> : null}
      </div>
    </LoadingOrEmptyWrapper>
  );
};
